import { createSignal, For, Show } from 'solid-js'
import { openBrowse, openFile, route, useTree } from '../store/view.ts'
import { FileTree } from './FileTree.tsx'
import { PathBreadcrumb } from './PathBreadcrumb.tsx'
import { ReadmePanel } from './ReadmePanel.tsx'
import { Splitter } from './Splitter.tsx'

export function BrowseView() {
  const target = () => route() as { repo: string, path: string }
  const entries = useTree(() => target().repo, () => target().path)
  const [treeWidth, setTreeWidth] = createSignal(280)

  return (
    <div class="h-full flex overflow-hidden">
      <div class="shrink-0 overflow-hidden border-r border-border" style={{ width: `${treeWidth()}px` }}>
        <FileTree repo={target().repo} activePath={target().path} />
      </div>
      <Splitter onResize={setTreeWidth} />

      <div class="min-w-0 flex flex-1 flex-col overflow-hidden">
        <div class="flex shrink-0 items-center gap-3 border-b border-border bg-muted/40 px-2 py-1">
          <PathBreadcrumb repo={target().repo} path={target().path} />
        </div>

        <div class="min-h-0 flex-1 overflow-auto">
          {/* the readme comes first, the way code search opens a folder on it */}
          <ReadmePanel repo={target().repo} path={target().path} />

          <Show when={target().path}>
            <div
              class="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-1 text-xs transition-colors hover:bg-muted/50"
              onClick={() => openBrowse(target().repo, target().path.split('/').slice(0, -1).join('/'))}
            >
              <div class="i-lucide-corner-left-up size-3.5 shrink-0 text-muted-foreground" />
              <span class="font-mono">..</span>
            </div>
          </Show>

          {/*
            "empty directory" is only true once something actually loaded. keying the
            fallback off an undefined `data` instead makes every not-yet-cached folder
            flash "empty" on the way in
          */}
          <For
            each={entries.data ?? []}
            fallback={(
              <Show when={entries.data} fallback={<div class="p-4 text-sm text-muted-foreground">loading...</div>}>
                <div class="p-4 text-sm text-muted-foreground">empty directory</div>
              </Show>
            )}
          >
            {entry => (
              <div
                class="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-1 text-xs transition-colors hover:bg-muted/50"
                onClick={() => (entry.isDir
                  ? openBrowse(target().repo, entry.path)
                  : openFile(target().repo, entry.path))}
              >
                <div
                  class={entry.isDir
                    ? 'i-lucide-folder size-3.5 shrink-0 text-muted-foreground'
                    : 'i-lucide-file size-3.5 shrink-0 text-muted-foreground'}
                />
                <span class="truncate font-mono">{entry.name}</span>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  )
}
